import { reviewLocalBase, reviewRequest } from './reviewRequest';
import axios from 'axios';

import { API } from '@/config/constants';



export const isOfflineDraftRun = (run) => (
  run?.origin === 'local_desktop' || run?.is_offline_draft === true
);


const normalizeOfflineDraftStop = (stop) => {
  const resolved = stop?.resolved_order || {};
  const ref = resolved.order_ref || stop?.order_ref || {};
  const order = resolved.order || {};
  const orderId = order.id || ref.resolved_cloud_order_id || ref.order_id || stop?.order_id;

  return {
    id: stop.id,
    local_stop_id: stop.id,
    order_id: orderId,
    sequence: stop.sequence,
    order: {
      ...order,
      id: orderId,
      internal_order_number:
        order.internal_order_number
        || ref.cloud_internal_order_number
        || ref.provisional_internal_order_number
        || ref.temporary_label
        || 'Offline order',
      desktop_sync: order.desktop_sync || (
        ref.type === 'local_order'
          ? { origin: 'local', temporary_label: ref.temporary_label }
          : order.desktop_sync
      ),
    },
    order_ref: ref,
    resolved_order: resolved,
  };
};


export const normalizeOfflineDraftRun = (run) => {
  if (!run) return run;
  const stops = (run.stops || []).map(normalizeOfflineDraftStop);
  return {
    ...run,
    plan_revision: run.plan_revision ?? 0,
    status: run.state === 'ready_for_review' ? 'draft' : (run.status || run.state || 'draft'),
    state: run.state || 'draft',
    is_offline_draft: true,
    user_id: run.user_id || null,
    stops,
    populated_stops: stops,
  };
};


const runAction = (runId, action, config) => reviewRequest('post', `${API}/runs/${runId}/${action}`, undefined, config);

// Local mutation identity is captured by the caller, never looked up at send time.
const localPlanRequest = (method, url, data, config) => {
  const { runPlan, ...requestConfig } = config || {};
  return reviewRequest(method, url, runPlan ? { ...data, ...runPlan } : data, config ? requestConfig : undefined);
};

// Keep the historical optional second argument for list readers while stripping
// review-only metadata and honoring the captured destination for owned readers.
const readRunList = async (url, date, config) => {
  const options = date ? { ...config, params: { ...config?.params, date } } : config;
  if (config) return reviewRequest('get', url, undefined, options);
  return (await axios.get(url, options)).data;
};


export const getRuns = (date, config) => readRunList(`${API}/runs`, date, config);

export const getMyRuns = (date, config) => readRunList(`${API}/runs/my-runs`, date, config);

export const getRun = (runId, config) => reviewRequest('get', `${API}/runs/${runId}`, undefined, config);

export const createRun = (name, date, config) => reviewRequest('post', `${API}/runs`, { name, date }, config);

export const renameRun = (runId, name, config) => reviewRequest('put', `${API}/runs/${runId}`, { name }, config);

export const deleteRun = (runId, config) => reviewRequest('delete', `${API}/runs/${runId}`, undefined, config);

export const replayOfflineDraftRun = (payload, config) => reviewRequest('post', `${API}/runs/replay-offline-draft`, payload, config);

export const getOfflineDraftRuns = async (date, config) => {
  const runs = await readRunList(`${reviewLocalBase(config)}/api/desktop/draft-runs`, date, config);
  return runs.map(normalizeOfflineDraftRun);
};

export const getOfflineDraftRun = (runId, config) => reviewRequest('get', `${reviewLocalBase(config)}/api/desktop/draft-runs/${runId}`, undefined, config).then(normalizeOfflineDraftRun);

export const getCachedRuns = (date, config) => readRunList(`${reviewLocalBase(config)}/api/desktop/cached-runs`, date, config);

export const getCachedRun = (runId, config) => reviewRequest('get', `${reviewLocalBase(config)}/api/desktop/cached-runs/${runId}`, undefined, config);

export const renameCachedRunProposal = (runId, name, runCacheUpdatedAt = null, config) => localPlanRequest('put', `${reviewLocalBase(config)}/api/desktop/cached-runs/${runId}/proposal`, { name, run_cache_updated_at: runCacheUpdatedAt }, config);
export const assignCachedRunProposalUser = (runId, userId, runCacheUpdatedAt = null, config) => localPlanRequest('post', `${reviewLocalBase(config)}/api/desktop/cached-runs/${runId}/proposal/assign-user`, { user_id: userId, run_cache_updated_at: runCacheUpdatedAt }, config);
export const unassignCachedRunProposalUser = (runId, runCacheUpdatedAt = null, config) => localPlanRequest('post', `${reviewLocalBase(config)}/api/desktop/cached-runs/${runId}/proposal/unassign-user`, { run_cache_updated_at: runCacheUpdatedAt }, config);
export const addCachedRunProposalStop = (runId, orderId, runCacheUpdatedAt = null, config) => localPlanRequest('post', `${reviewLocalBase(config)}/api/desktop/cached-runs/${runId}/proposal/stops`, { order_id: orderId, run_cache_updated_at: runCacheUpdatedAt }, config);
export const removeCachedRunProposalStop = (runId, orderId, config) => localPlanRequest('delete', `${reviewLocalBase(config)}/api/desktop/cached-runs/${runId}/proposal/stops/${orderId}`, undefined, config);
export const reorderCachedRunProposalStops = (runId, orderIds, runCacheUpdatedAt = null, config) => localPlanRequest('put', `${reviewLocalBase(config)}/api/desktop/cached-runs/${runId}/proposal/stops/order`, { order_ids: orderIds, run_cache_updated_at: runCacheUpdatedAt }, config);
export const discardCachedRunProposal = (runId, config) => localPlanRequest('delete', `${reviewLocalBase(config)}/api/desktop/cached-runs/${runId}/proposal`, undefined, config);

export const createOfflineDraftRun = (name, date, config) => reviewRequest('post', `${reviewLocalBase(config)}/api/desktop/draft-runs`, { name, date }, config).then(normalizeOfflineDraftRun);

export const renameOfflineDraftRun = (runId, name, config) => localPlanRequest('put', `${reviewLocalBase(config)}/api/desktop/draft-runs/${runId}`, { name }, config).then(normalizeOfflineDraftRun);

export const discardOfflineDraftRun = (runId, config) => localPlanRequest('delete', `${reviewLocalBase(config)}/api/desktop/draft-runs/${runId}`, undefined, config);

export const getOfflineDraftRunReviewItems = (config) => reviewRequest('get', `${reviewLocalBase(config)}/api/desktop/reconciliation/draft-runs`, undefined, config);

export const completeOfflineDraftRunReplay = (runId, cloudRun, config) => localPlanRequest('post', `${reviewLocalBase(config)}/api/desktop/reconciliation/draft-runs/${runId}/complete`, { cloud_run: cloudRun }, config);

export const recordOfflineDraftRunReplayError = (runId, detail, config) => localPlanRequest('post', `${reviewLocalBase(config)}/api/desktop/reconciliation/draft-runs/${runId}/replay-error`, { detail }, config);

export const commitRun = (runId, config) => runAction(runId, 'commit', config);
export const uncommitRun = (runId, config) => runAction(runId, 'uncommit', config);
export const closeRun = (runId, config) => runAction(runId, 'close', config);
export const startRun = (runId, config) => runAction(runId, 'start', config);
export const unstartRun = (runId, config) => runAction(runId, 'unstart', config);

export const addRunStop = (runId, orderId, config) => reviewRequest('post', `${API}/runs/${runId}/add-stop`, { order_id: orderId }, config);

// Admin recovery support: what actually backs this order's hold. `can_clear`
// is decided server-side by the same check the repair enforces -- do not
// re-derive it here or the button and the endpoint will drift apart.
export const getOrderReservationHold = (orderId, config) => reviewRequest('get', `${API}/runs/reservations/${orderId}`, undefined, config);

// Admin recovery: clear a run reservation that no live run backs. The backend
// refuses if the reservation is real, so callers may offer this on any add-stop
// conflict and let the server adjudicate.
export const repairOrderReservation = (orderId, config) => reviewRequest('post', `${API}/runs/reservations/${orderId}/repair`, undefined, config);

export const removeRunStop = (runId, orderId, config) => reviewRequest('post', `${API}/runs/${runId}/remove-stop`, { order_id: orderId }, config);

export const reorderRunStops = (runId, orderIds, config) => reviewRequest('post', `${API}/runs/${runId}/reorder-stops`, { order_ids: orderIds }, config);

export const addOfflineDraftRunStop = (runId, orderId, config) => localPlanRequest('post', `${reviewLocalBase(config)}/api/desktop/draft-runs/${runId}/stops`, { order_id: orderId }, config).then(normalizeOfflineDraftRun);

export const removeOfflineDraftRunStop = (runId, stopId, config) => localPlanRequest('delete', `${reviewLocalBase(config)}/api/desktop/draft-runs/${runId}/stops/${stopId}`, undefined, config).then(normalizeOfflineDraftRun);

export const reorderOfflineDraftRunStops = (runId, stopIds, config) => localPlanRequest('put', `${reviewLocalBase(config)}/api/desktop/draft-runs/${runId}/stops/order`, { stop_ids: stopIds }, config).then(normalizeOfflineDraftRun);

export const getCachedRunProposalReviewItems = (config) => reviewRequest('get', `${reviewLocalBase(config)}/api/desktop/reconciliation/cached-run-proposals`, undefined, config);

export const recordCachedRunProposalDecision = (runId, payload, config) => reviewRequest('post', `${reviewLocalBase(config)}/api/desktop/reconciliation/cached-run-proposals/${runId}/decision`, payload, config);

export const reconcileCachedRunProposal = (runId, payload, config) => reviewRequest('post', `${API}/runs/${runId}/reconcile-offline-proposal`, payload, config);

export const recordCachedRunProposalCloudResult = (runId, payload, config) => reviewRequest('post', `${reviewLocalBase(config)}/api/desktop/reconciliation/cached-run-proposals/${runId}/cloud-result`, payload, config);

export const assignOfflineDraftRunUser = (runId, userId, config) => localPlanRequest('post', `${reviewLocalBase(config)}/api/desktop/draft-runs/${runId}/assign-user`, { user_id: userId }, config).then(normalizeOfflineDraftRun);

export const unassignOfflineDraftRunUser = (runId, config) => localPlanRequest('post', `${reviewLocalBase(config)}/api/desktop/draft-runs/${runId}/unassign-user`, undefined, config).then(normalizeOfflineDraftRun);

export const assignRunUser = (runId, userId, config) => reviewRequest('post', `${API}/runs/${runId}/assign-user`, { user_id: userId }, config);

export const unassignRunUser = (runId, config) => reviewRequest('post', `${API}/runs/${runId}/unassign-user`, undefined, config);

export const notifyRunDriver = (runId, config) => reviewRequest('post', `${API}/runs/${runId}/notify-driver`, undefined, config);

// Durable run sends have a separate read/check path. A lookup never calls replay.
const runSends = config => `${reviewLocalBase(config)}/api/desktop/reconciliation/run-sends`;
export const getRunSendAttempts = (cursor, config) => reviewRequest('get', runSends(config), undefined,
  { ...config, params: { limit: 50, ...(cursor ? { after: cursor } : {}) } });
export const getRunSendAttempt = (id, config) => reviewRequest('get', `${runSends(config)}/${encodeURIComponent(id)}`, undefined, config);
export const prepareRunSend = (request, config) => reviewRequest('post', `${runSends(config)}/prepare`, request, config);
export const recordRunSendOutcome = (id, outcome, config) => reviewRequest('post', `${runSends(config)}/${encodeURIComponent(id)}/outcome`, { outcome }, config);
export const settleRunSend = (id, config) => reviewRequest('post', `${runSends(config)}/${encodeURIComponent(id)}/settle`, {}, config);
export const sendRunAttempt = (envelope, config) => reviewRequest('post', `${API}/runs/offline-send-attempts`, envelope, config);
export const getRunSendOutcome = (id, config) => reviewRequest('get', `${API}/runs/offline-send-attempts/${encodeURIComponent(id)}`, undefined, config);
export const closeRunSendAttempt = (id, envelope, config) => reviewRequest('post', `${API}/runs/offline-send-attempts/${encodeURIComponent(id)}/close`, envelope, config);
